import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios";
import { Link } from "react-router-dom";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import "react-confirm-alert/src/react-confirm-alert.css";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import Pagination from "../../components/AdminSideComponents/AdminPagination";
import DeleteConfirmationModal from "../../components/AdminSideComponents/Modals/DeleteConfirmationModal";
import toast from "react-hot-toast";
import AdminProductModal from "../../components/AdminSideComponents/Modals/AdminProductModal";
import HoverCarousel from "../../components/Products/HoverCarousel";

const Products = () => {
  const ITEMS_PER_PAGE = 8;

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // FETCH PRODUCTS
  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get("/products/all");
      setProducts(response.data);
      setFilteredProducts(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      console.error("Error fetching products:", err);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const newTotalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages || 1);
    }
  }, [filteredProducts, currentPage]);

  // SEARCH HANDLER
  const onSearchHandler = (query) => {
    setSearchTerm(query);
    const lowerCaseQuery = query.trim().toLowerCase();
    const results = products.filter((product) =>
      product.name.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredProducts(results);
    setCurrentPage(1);
  };

  // DELETE HANDLERS
  const handleOpenDeleteModal = (id) => {
    setDeletingProductId(id);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeletingProductId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingProductId) return;
    try {
      setIsDeleting(true);
      await axiosInstance.delete(`/products/delete/${deletingProductId}`);
      const updatedList = products.filter(
        (product) => product._id !== deletingProductId
      );
      setProducts(updatedList);
      setFilteredProducts(
        searchTerm
          ? updatedList.filter((product) =>
              product.name
                .toLowerCase()
                .includes(searchTerm.trim().toLowerCase())
            )
          : updatedList
      );
      toast.success("Product deleted successfully", { id: deletingProductId });
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete. Try again.");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setDeletingProductId(null);
    }
  };

  const onAddNewHandler = () => {
    setEditData(null);
    setModalOpen(true);
  };

  // HANDLE EDIT CLICK
  const handleEditClick = (product) => {
    setEditData(product);
    setModalOpen(true);
  };

  // HANDLE PRODUCT SUBMISSION (CREATE/UPDATE)
  const handleProductSubmit = async (productData) => {
    setIsSubmitting(true);
    try {
      if (editData) {
        // Update existing product
        const updatedProduct = await axiosInstance.put(
          `/products/update/${editData._id}`,
          productData
        );
        setProducts(
          products.map((p) =>
            p._id === editData._id ? updatedProduct.data : p
          )
        );
        setFilteredProducts(
          filteredProducts.map((p) =>
            p._id === editData._id ? updatedProduct.data : p
          )
        );
        toast.success("Product updated successfully");
      } else {
        // Create new product
        const newProduct = await axiosInstance.post(
          "/products/create",
          productData
        );
        setProducts([newProduct.data, ...products]);
        setFilteredProducts([newProduct.data, ...filteredProducts]);
        toast.success("Product created successfully");
      }
      setModalOpen(false);
    } catch (err) {
      console.error("Error saving product:", err);
      toast.error(err.response?.data?.message || "Failed to save product");
    } finally {
      setIsSubmitting(false);
      fetchProducts();
    }
  };

  // PAGINATION CALC
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
      <div className="h-48 bg-gray-200"></div>
      <div className="p-4">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
        <div className="h-3 bg-gray-200 rounded w-5/6"></div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <AdminSearchBar
          title="Products Management"
          onSearchClick={onSearchHandler}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
          {Array.from({ length: ITEMS_PER_PAGE }).map((_, idx) => (
            <SkeletonCard key={idx} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <AdminSearchBar
          title="Products Management"
          onSearchClick={onSearchHandler}
        />
        <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg flex items-center justify-between">
          <div>
            <p className="font-semibold">Failed to load products</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
          <button
            onClick={fetchProducts}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 px-4 pr-4">
      {/* SEARCH BAR */}
      <AdminSearchBar
        title="Products Management"
        onSearchClick={onSearchHandler}
        onAddNewClick={onAddNewHandler}
      />

      {paginatedProducts.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-600">
            No products found
          </h3>
          <p className="text-gray-500 mt-2">
            Click the button above to add your first product
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {paginatedProducts.map((product) => (
            <div
              key={product._id}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition duration-300 flex flex-col  relative"
            >
              {/* IMAGE */}
              <div className="relative h-48 bg-gray-100">
                <HoverCarousel images={product.images} alt={product.name} />
              </div>

              {/* CONTENT */}
              <div className="p-4 flex-1 flex flex-col justify-between group">
                <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">
                  {product.name}
                </h3>
                <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                  {product.description}
                </p>
                {/* BUTTONS: slide-up on hover */}
                <div
                  className="absolute bottom-0 left-0 w-full flex justify-end gap-2 transform translate-y-full opacity-0 
             bg-white bg-opacity-70 py-2 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 px-4"
                >
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleEditClick(product);
                    }}
                    className="flex items-center justify-center transition 
             text-primaryBtnColor hover:text-primaryBtnHoverColor
             shadow hover:shadow-md"
                  >
                    <FiEdit size={20} />
                  </button>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpenDeleteModal(product._id);
                    }}
                    className="flex items-center justify-center transition 
             text-red-800 hover:text-red-700
             shadow hover:shadow-md"
                  >
                    <FiTrash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PAGINATION */}
      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalPages={totalPages}
      />

      {/* DELETE CONFIRMATION MODAL */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        loading={isDeleting}
        itemName={"this Product "}
      />

      {/* PRODUCT FORM MODAL */}
      <AdminProductModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleProductSubmit}
        initialData={editData}
        isEditMode={!!editData}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Products;
